const formatBallotChoice = (choice, referendum = choice.referendum) =>
  (choice.candidateName ? choice.candidateName : '')
  + (choice.ballotCandidateName ? choice.ballotCandidateName : '')
  + (choice.ballotCandidatePartyName ? ' - ' + choice.ballotCandidatePartyName : '')
  + (
    choice.ballotCandidateId
    && !choice.ballotCandidatePartyName
    && !referendum
      ? ' - Independent'
      : ''
  )
  + (
    !choice.ballotCandidateId
    && !referendum
    && choice.candidateName
      ? ' (Write-In)'
      : ''
  );

const getBallotChoiceKey = (choice) => {
  if (choice.ballotCandidateId) {
    return `candidate-${choice.ballotCandidateId}`;
  }

  return `write-in-${choice.candidateName || choice.ballotCandidateName || 'unknown'}`;
};

export const buildBallotResults = (ballots = [], results = []) => {
  const resultsByBallot = new Map();
  const resultBallotMetadata = new Map();

  results.forEach((result) => {
    const ballotId = result.ballotId;
    const ballotChoices = resultsByBallot.get(ballotId) || new Map();
    const key = getBallotChoiceKey(result);
    const currentChoice = ballotChoices.get(key);
    const count = Number(result.cnt) || 0;

    ballotChoices.set(key, {
      key,
      label: formatBallotChoice(result, result.referendum),
      count: currentChoice ? currentChoice.count + count : count,
    });

    resultsByBallot.set(ballotId, ballotChoices);
    if (!resultBallotMetadata.has(ballotId)) {
      resultBallotMetadata.set(ballotId, {
        ballotId,
        ballotName: result.ballotName || `Ballot ${ballotId}`,
        referendum: Boolean(result.referendum),
        numberChosen: 0,
        allCandidates: [],
      });
    }
  });

  const configuredBallots = [...ballots].sort(
    (left, right) => (left.sortOrder || 0) - (right.sortOrder || 0)
  );
  const configuredBallotIds = new Set(configuredBallots.map((ballot) => ballot.ballotId));
  const resultOnlyBallots = Array.from(resultBallotMetadata.values())
    .filter((ballot) => !configuredBallotIds.has(ballot.ballotId))
    .sort((left, right) => left.ballotName.localeCompare(right.ballotName));
  const orderedBallots = configuredBallots.length
    ? [...configuredBallots, ...resultOnlyBallots]
    : resultOnlyBallots;

  return orderedBallots.map((ballot) => {
    const mergedChoices = new Map();

    (ballot.allCandidates || []).forEach((candidate) => {
      const key = getBallotChoiceKey(candidate);
      mergedChoices.set(key, {
        key,
        label: formatBallotChoice(candidate, ballot.referendum),
        count: 0,
      });
    });

    const resultChoices = resultsByBallot.get(ballot.ballotId);
    if (resultChoices) {
      resultChoices.forEach((choice, key) => {
        const existingChoice = mergedChoices.get(key);

        mergedChoices.set(
          key,
          existingChoice ? { ...existingChoice, count: choice.count } : choice
        );
      });
    }

    const rows = Array.from(mergedChoices.values()).sort(
      (left, right) => right.count - left.count || left.label.localeCompare(right.label)
    );
    const winnerCount = Math.max(0, Number(ballot.numberChosen) || 0);
    const rankedRows = rows.filter((row) => row.count > 0);
    const cutoffRow = winnerCount && rankedRows.length
      ? rankedRows[Math.min(winnerCount, rankedRows.length) - 1]
      : null;
    const winnerKeys = cutoffRow
      ? new Set(
          rankedRows
            .filter((row) => row.count >= cutoffRow.count)
            .map((row) => row.key)
        )
      : new Set();

    return {
      ballotId: ballot.ballotId,
      ballotName: ballot.ballotName,
      rows: rows.map((row) => ({
        ...row,
        isLeader: winnerKeys.has(row.key),
      })),
    };
  });
};
